import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { LANGS } from "@/i18n/translations";
import BrandLogo from "@/components/BrandLogo";
import { Menu, X, Globe, ChevronDown, LogIn, LogOut, User } from "lucide-react";

export default function Header() {
  const { lang, setLang, t } = useLang();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  const links = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/about", label: t.nav.about, id: "about" },
    { to: "/register", label: t.nav.register, id: "register" },
    { to: "/slots", label: t.nav.zoom, id: "zoom" },
    { to: "/materials", label: "Materiali", id: "materials" },
    { to: "/exercises", label: t.nav.exercises, id: "exercises" },
    { to: "/translator", label: t.nav.translator, id: "translator" },
  ];

  return (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-[#E4EAF7]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
        <BrandLogo size="md" />

        <nav className="hidden xl:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive ? "brand-blue-bg text-white" : "text-[#0F1E3D] hover:bg-[#EEF4FF]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setLangOpen((v) => !v)} className="btn-ghost !py-2 !px-3 text-sm" data-testid="lang-switcher-btn" aria-label="Language">
              <Globe size={16} />
              <span className="uppercase font-bold">{lang}</span>
              <ChevronDown size={14} />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-2 w-40 card-elevated p-2 z-50" data-testid="lang-menu">
                {LANGS.map((l) => (
                  <button key={l.code} onClick={() => { setLang(l.code); setLangOpen(false); }}
                    data-testid={`lang-option-${l.code}`}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-[#EEF4FF] flex items-center justify-between ${lang === l.code ? "bg-[#EEF4FF] font-semibold" : ""}`}>
                    <span>{l.label}</span>
                    <span className="text-xs text-[#F97316] font-bold">{l.flag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              <NavLink to="/dashboard" className="btn-ghost !py-2 !px-3 text-sm" data-testid="user-badge">
                <User size={14} /> <span className="max-w-[100px] truncate">{user.nome}</span>
              </NavLink>
              <button onClick={logout} className="btn-ghost !py-2 !px-3 text-sm" data-testid="logout-btn" aria-label="Logout">
                <LogOut size={14} />
              </button>
            </div>
          ) : (
            <>
              <Link to="/login" className="hidden md:inline-flex btn-ghost !py-2 !px-3 text-sm" data-testid="header-login-btn">
                <LogIn size={14} /> Login
              </Link>
              <NavLink to="/register" className="hidden md:inline-flex btn-accent !py-2 !px-4 text-sm" data-testid="header-register-cta">
                €30
              </NavLink>
            </>
          )}

          <button className="xl:hidden btn-ghost !px-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-[#E4EAF7] bg-white" data-testid="mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${l.id}`}
                className={({ isActive }) => `px-3 py-2 rounded-lg text-sm font-medium ${isActive ? "brand-blue-bg text-white" : "text-[#0F1E3D] hover:bg-[#EEF4FF]"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="border-t border-[#E4EAF7] my-2" />
            {user ? (
              <button onClick={() => { logout(); setOpen(false); }} className="text-left px-3 py-2 rounded-lg text-sm font-medium text-[#F97316]"><LogOut size={14} className="inline mr-2" /> Logout ({user.nome})</button>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 rounded-lg text-sm font-medium text-[#0B3D91]"><LogIn size={14} className="inline mr-2" /> Login</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
